import { useState, useEffect, useCallback } from "react"
import {
  testGeneration,
  loadSeasonStructure,
  updateSeasonStructure,
  loadAllPhilosophySections,
  updatePhilosophySection,
  getUserProfile,
  PHILOSOPHY_SECTIONS,
  PHILOSOPHY_LABELS,
  type PhilosophySection,
} from "@/lib/api"
import { toast } from "sonner"
import type { Season } from "@/types/workout"
import { SafetyDialog } from "./ConfirmDialog"
import { UserCombobox, type UserOption } from "./user-combobox"
import { UserProfileCard } from "./user-profile-card"
import { RefreshCw } from "lucide-react"
import { useReadOnly } from "@/lib/read-only"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"

function StatusBadge({
  loaded,
  isLoading,
}: {
  loaded: boolean | null
  isLoading: boolean
}) {
  if (loaded === null && isLoading) {
    return (
      <span className="flex items-center gap-1 text-xs text-muted-foreground">
        <RefreshCw className="size-3 animate-spin" /> Loading...
      </span>
    )
  }
  if (loaded === true) {
    return (
      <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 font-medium">
        Loaded from Supabase
      </span>
    )
  }
  if (loaded === false) {
    return (
      <span className="text-xs px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-700 font-medium">
        Using local template
      </span>
    )
  }
  return null
}

type ConfigTab = "philosophy" | "structure"

export function PrinciplesAdmin() {
  const { readOnly } = useReadOnly()

  // Top-level tab state lifted out of Radix so we can fetch on tab change.
  // The previous `onFocusCapture` only fired when descendants gained focus
  // — clicking a tab trigger never focused the content, so the legacy
  // Principles / Season Structure tabs stayed blank until the admin
  // clicked into the textarea. That looked like "I can't edit principles."
  const [activeTab, setActiveTab] = useState<ConfigTab>("philosophy")

  const [seasonStructureContent, setSeasonStructureContent] = useState("")
  const [structureLoadedFromServer, setStructureLoadedFromServer] = useState<
    boolean | null
  >(null)
  const [isStructureLoading, setIsStructureLoading] = useState(false)
  const [showStructureConfirmDialog, setShowStructureConfirmDialog] =
    useState(false)

  const [selectedUser, setSelectedUser] = useState<UserOption | null>(null)
  const [selectedUserProfile, setSelectedUserProfile] = useState<Record<
    string,
    unknown
  > | null>(null)

  const [philosophySections, setPhilosophySections] = useState<
    Record<string, string>
  >({})
  const [philosophyLoading, setPhilosophyLoading] = useState(false)
  const [philosophyLoaded, setPhilosophyLoaded] = useState<boolean | null>(null)
  const [activeSectionTab, setActiveSectionTab] = useState<PhilosophySection>("A")
  const [savingSection, setSavingSection] = useState<string | null>(null)
  const [showPhilosophyConfirm, setShowPhilosophyConfirm] = useState(false)

  const [testResult, setTestResult] = useState<{
    response: string
    season: Season
    note: string
  } | null>(null)
  const [isTesting, setIsTesting] = useState(false)

  const fetchPhilosophySections = useCallback(async () => {
    setPhilosophyLoading(true)
    try {
      const sections = await loadAllPhilosophySections()
      const mapped: Record<string, string> = {}
      for (const key of PHILOSOPHY_SECTIONS) {
        mapped[key] = sections[key] ?? ""
      }
      setPhilosophySections(mapped)
      setPhilosophyLoaded(true)
    } catch {
      toast.error("Failed to load philosophy sections")
      setPhilosophyLoaded(false)
    } finally {
      setPhilosophyLoading(false)
    }
  }, [])

  const handleSavePhilosophySection = async () => {
    const section = activeSectionTab
    const content = philosophySections[section]
    if (!content?.trim()) {
      toast.error(`Section ${section} content cannot be empty`)
      return
    }
    setSavingSection(section)
    try {
      const result = await updatePhilosophySection(section, content)
      toast.success(result.message)
      toast.info(result.note, { duration: 8000 })
      setShowPhilosophyConfirm(false)
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : `Failed to update section ${section}`,
      )
    } finally {
      setSavingSection(null)
    }
  }

  const fetchSeasonStructure = useCallback(async () => {
    setIsStructureLoading(true)
    try {
      const content = await loadSeasonStructure()
      setSeasonStructureContent(content)
      setStructureLoadedFromServer(true)
    } catch {
      toast.error("Failed to load season structure instructions")
      setStructureLoadedFromServer(false)
    } finally {
      setIsStructureLoading(false)
    }
  }, [])

  useEffect(() => {
    // Always load philosophy on mount (default tab).
    fetchPhilosophySections()
  }, [fetchPhilosophySections])

  // Lazy-load legacy editors the first time their tab is opened. The legacy
  // files are large enough that we don't want to fetch them on every page
  // visit, but the user must see the existing content before they can edit.
  useEffect(() => {
    if (
      activeTab === "structure" &&
      structureLoadedFromServer === null &&
      !isStructureLoading
    ) {
      fetchSeasonStructure()
    }
  }, [
    activeTab,
    structureLoadedFromServer,
    isStructureLoading,
    fetchSeasonStructure,
  ])

  const handleSelectUser = async (user: UserOption | null) => {
    setSelectedUser(user)
    setSelectedUserProfile(null)
    if (user) {
      try {
        const profile = await getUserProfile(user.id)
        setSelectedUserProfile(profile)
      } catch {
        toast.error("Failed to load user profile")
      }
    }
  }

  const handleTest = async () => {
    if (!selectedUser) {
      toast.error("Please select a user")
      return
    }

    setIsTesting(true)
    try {
      const result = await testGeneration(
        { id: selectedUser.id },
        seasonStructureContent.trim() || undefined,
      )
      setTestResult(result)
      toast.success("Generation tested successfully!")
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Failed to test generation",
      )
      setTestResult(null)
    } finally {
      setIsTesting(false)
    }
  }

  const handleStructureSubmit = async () => {
    if (!seasonStructureContent.trim()) {
      toast.error("Season structure content cannot be empty")
      return
    }
    setIsStructureLoading(true)
    try {
      const result = await updateSeasonStructure(seasonStructureContent)
      toast.success(result.message)
      toast.info(result.note, { duration: 8000 })
      setShowStructureConfirmDialog(false)
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : "Failed to update season structure",
      )
    } finally {
      setIsStructureLoading(false)
    }
  }

  return (
    <div className="p-6">
      <h2 className="text-2xl font-bold mb-6">Principles & Configuration</h2>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <div className="space-y-6">
          <Tabs
            value={activeTab}
            onValueChange={(v) => setActiveTab(v as ConfigTab)}
          >
            <TabsList>
              <TabsTrigger value="philosophy">
                Philosophy Sections
              </TabsTrigger>
              <TabsTrigger value="structure">
                Season Structure
              </TabsTrigger>
            </TabsList>

            <TabsContent value="philosophy" className="space-y-3 mt-4">
              <div className="flex items-center justify-between">
                <StatusBadge loaded={philosophyLoaded} isLoading={philosophyLoading} />
                <button
                  onClick={fetchPhilosophySections}
                  disabled={philosophyLoading}
                  title="Reload all sections from server"
                  className="p-1 rounded hover:bg-accent disabled:opacity-40 transition-colors"
                >
                  <RefreshCw
                    className={`size-4 text-muted-foreground ${philosophyLoading ? "animate-spin" : ""}`}
                  />
                </button>
              </div>

              <div className="flex flex-wrap gap-1 border-b pb-2">
                {PHILOSOPHY_SECTIONS.map((key) => (
                  <button
                    key={key}
                    onClick={() => setActiveSectionTab(key)}
                    className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                      activeSectionTab === key
                        ? "bg-primary text-primary-foreground"
                        : "bg-muted text-muted-foreground hover:bg-accent"
                    }`}
                  >
                    {key}: {PHILOSOPHY_LABELS[key]}
                  </button>
                ))}
              </div>

              <div className="space-y-2">
                <p className="text-xs text-muted-foreground">
                  Section {activeSectionTab}: {PHILOSOPHY_LABELS[activeSectionTab]} -- Used by pipeline Step {
                    { A: "2", B: "3", C: "4", D: "5", E: "6", F: "7", G: "All validation" }[activeSectionTab]
                  }
                </p>
                <textarea
                  value={philosophySections[activeSectionTab] ?? ""}
                  onChange={(e) =>
                    setPhilosophySections((prev) => ({
                      ...prev,
                      [activeSectionTab]: e.target.value,
                    }))
                  }
                  className="w-full h-96 px-3 py-2 border rounded-md font-mono text-sm focus:ring-2 focus:ring-primary bg-background"
                  placeholder={`# Section ${activeSectionTab}: ${PHILOSOPHY_LABELS[activeSectionTab]}...`}
                  disabled={philosophyLoading}
                />
                <div className="flex justify-end">
                  <button
                    onClick={() => setShowPhilosophyConfirm(true)}
                    disabled={readOnly || philosophyLoading || savingSection !== null}
                    title={readOnly ? "Read-only mode active" : undefined}
                    className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 transition-colors text-sm"
                  >
                    {savingSection === activeSectionTab
                      ? "Saving..."
                      : `Update Section ${activeSectionTab}`}
                  </button>
                </div>
              </div>
            </TabsContent>

            <TabsContent
              value="structure"
              className="space-y-3 mt-4"
            >
              <div className="rounded-md bg-yellow-50 border border-yellow-200 px-3 py-2 mb-2">
                <p className="text-xs text-yellow-700">
                  Legacy season structure editor. Season structure is now derived from phase logic in the agentic pipeline.
                </p>
              </div>
              <div className="flex items-center justify-between">
                <StatusBadge
                  loaded={structureLoadedFromServer}
                  isLoading={isStructureLoading}
                />
                <button
                  onClick={fetchSeasonStructure}
                  disabled={isStructureLoading}
                  title="Reload from server"
                  className="p-1 rounded hover:bg-accent disabled:opacity-40 transition-colors"
                >
                  <RefreshCw
                    className={`size-4 text-muted-foreground ${isStructureLoading ? "animate-spin" : ""}`}
                  />
                </button>
              </div>
              <textarea
                value={seasonStructureContent}
                onChange={(e) => setSeasonStructureContent(e.target.value)}
                className="w-full h-96 px-3 py-2 border rounded-md font-mono text-sm focus:ring-2 focus:ring-primary bg-background"
                placeholder="## Season Structure Guidelines..."
                disabled={isStructureLoading}
              />
              <div className="flex justify-end">
                <button
                  onClick={() => setShowStructureConfirmDialog(true)}
                  disabled={readOnly || isStructureLoading || isTesting}
                  title={readOnly ? "Read-only mode active" : undefined}
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 transition-colors text-sm"
                >
                  Update Season Structure
                </button>
              </div>
            </TabsContent>
          </Tabs>

          <div className="space-y-3 rounded-lg border p-4">
            <h3 className="font-semibold">Test Generation</h3>
            <div className="flex items-end gap-3">
              <div className="flex-1">
                <label className="block text-sm font-medium text-muted-foreground mb-1">
                  Test User
                </label>
                <UserCombobox
                  value={selectedUser}
                  onSelect={handleSelectUser}
                  placeholder="Select test user..."
                />
              </div>
              <button
                onClick={handleTest}
                disabled={isTesting || !selectedUser}
                className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 disabled:opacity-50 transition-colors text-sm"
              >
                {isTesting ? "Testing..." : "Test Generation"}
              </button>
            </div>
            {selectedUser && selectedUserProfile && (
              <UserProfileCard profile={selectedUserProfile} compact />
            )}
          </div>
        </div>

        <div>
          <h3 className="text-lg font-semibold mb-4">Test Results</h3>

          {!testResult && (
            <div className="flex items-center justify-center rounded-lg border bg-card p-12">
              <p className="text-muted-foreground text-center">
                Test results will appear here after clicking "Test Generation"
              </p>
            </div>
          )}

          {testResult && (
            <div className="rounded-lg border bg-card p-6 space-y-4">
              <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
                <p className="text-sm text-yellow-800 font-medium">
                  Test Mode - Plan Not Saved
                </p>
                <p className="text-xs text-yellow-700 mt-1">
                  {testResult.note}
                </p>
              </div>

              <div>
                <h4 className="font-semibold mb-2">AI Response:</h4>
                <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                  {testResult.response}
                </p>
              </div>

              <div>
                <h4 className="font-semibold mb-2">Generated Plan:</h4>
                <div className="bg-muted rounded-md p-4 space-y-2 text-sm">
                  <p>
                    <strong>Goal:</strong> {testResult.season.goal}
                  </p>
                  <p>
                    <strong>Priorities:</strong>{" "}
                    {testResult.season.priorities.join(", ")}
                  </p>
                  <p>
                    <strong>Cycles:</strong> {testResult.season.cycles.length}
                  </p>
                  <p>
                    <strong>Weeks:</strong>{" "}
                    {testResult.season.cycles[0]?.weeks.length || 0}
                  </p>
                </div>
              </div>

              <details>
                <summary className="cursor-pointer text-sm font-medium text-muted-foreground hover:text-foreground">
                  View Full Season JSON
                </summary>
                <pre className="mt-2 bg-foreground text-background rounded-md p-4 text-xs overflow-auto max-h-96">
                  {JSON.stringify(testResult.season, null, 2)}
                </pre>
              </details>
            </div>
          )}
        </div>
      </div>

      {showPhilosophyConfirm && (
        <SafetyDialog
          title={`Update Philosophy Section ${activeSectionTab}`}
          description={`You are about to update Section ${activeSectionTab}: ${PHILOSOPHY_LABELS[activeSectionTab]} in Supabase Storage.`}
          warnings={[
            "This changes AI workout generation for ALL users",
            "The backend must be restarted to load new philosophy into memory",
            "All future workouts generated will use this updated section",
          ]}
          confirmLabel={`Update Section ${activeSectionTab}`}
          onConfirm={handleSavePhilosophySection}
          onCancel={() => setShowPhilosophyConfirm(false)}
          isLoading={savingSection !== null}
        />
      )}
      {showStructureConfirmDialog && (
        <SafetyDialog
          title="Update Season Structure"
          description="You are about to overwrite the season structure instructions in Supabase Storage."
          warnings={[
            "This changes how AI structures workout seasons",
            "The backend must be restarted for changes to take effect",
          ]}
          confirmLabel="Confirm Update"
          onConfirm={handleStructureSubmit}
          onCancel={() => setShowStructureConfirmDialog(false)}
          isLoading={isStructureLoading}
        />
      )}
    </div>
  )
}
